<script setup>
import { ref, computed } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import { useCurrency } from '@composables/useCurrency';
import { trans, transchoice } from '@elements/translation';
import { parseDateToString, isDateInThisMonth, getFirstOfMonthString } from '@tools/date';
import { currency } from '@elements/number-formatter';
import { eventBus } from '@tools/event-bus';
import Panel from '@molecules/components/Panel.vue';
import Button from '@atoms/components/Button.vue';
import { useSubscriptionStore } from '@organisms/ubiq-administration/store/subscriptions';

const { currencyField } = useCurrency();

const subscriptionStore = useSubscriptionStore();
const subscription = subscriptionStore.selectedSubscriptionData;

const price = ref(subscription.amount);
const localAmount = currencyField(price);

const amountError = ref(null);
const localStartDate = ref(new Date(subscription.start_date?.iso ?? subscription.start_date?.date));
const localEndDate = ref(subscription.end_date?.iso ?? subscription.end_date?.date);
const localIsUnlimited = ref(!localEndDate.value);
const displayNoEndConfirmation = ref(false);
const displayShortEndConfirmation = ref(false);
const displayLongEndConfirmation = ref(false);
const isLoading = ref(false);

const emit = defineEmits([ 'close' ]);

const hasSubscriptionOffices = computed(() => subscription.office_offers?.length > 0);

const validateAmount = () => {
  if (!localAmount.value || localAmount.value <= 0) {
    amountError.value = trans('gestion.adaf.slider.field.price.error_empty_zero');
    return;
  }

  amountError.value = null;
};

const onEndDateChange = (date) => {
  if (date) {
    localEndDate.value = date;
  }
};

const onUnlimitedChange = () => {
  if (localIsUnlimited.value) {
    localEndDate.value = null;
  }
};

const isValidateDisabled = computed(() => !localAmount.value || amountError.value || (!localEndDate.value && !localIsUnlimited.value));
const isInValidatingState = computed(() => displayNoEndConfirmation.value || displayShortEndConfirmation.value || displayLongEndConfirmation.value);
const minDisabledDate = computed(() => {
  const startDate = subscription.start_date?.date
    ? new Date(subscription.start_date.date)
    : null;

  const today = new Date();
  if (!startDate) {
    return today;
  }

  return startDate < today ? startDate : today;
});

const onClose = () => {
  if (isInValidatingState.value) {
    displayNoEndConfirmation.value = false;
    displayShortEndConfirmation.value = false;
    displayLongEndConfirmation.value = false;
    return;
  }

  emit('close');
};

const onValidate = async () => {
  validateAmount();

  if (isValidateDisabled.value) {
    return;
  }

  if (!isInValidatingState.value) {
    if (isDateInThisMonth(localEndDate.value)) {
      displayShortEndConfirmation.value = true;
      return;
    }
    if (localIsUnlimited.value) {
      displayNoEndConfirmation.value = true;
      return;
    }
    displayLongEndConfirmation.value = true;
    return;
  }

  isLoading.value = true;
  await subscriptionStore.updateSubscription({
    id: subscription.id,
    amount: price.value,
    endDate: localEndDate.value,
  });
  isLoading.value = false;
  eventBus.dispatchEvent(new Event('update-subcription-details-action'));
  displayShortEndConfirmation.value = false;
  displayNoEndConfirmation.value = false;
  displayLongEndConfirmation.value = false;
  emit('close');
};

</script>

<template>
  <div class="slider-edit-subscription">
    <div v-if="!isInValidatingState" class="slider-edit-subscription__content">
      <div class="slider-edit-subscription__form">
        <div>
          <label for="edit-subscription-amount-price">{{ trans('gestion.subscription.payment.amount') }}</label>
          <div class="input-icon">
            <input
              id="edit-subscription-amount-price"
              v-model="localAmount"
              :class="{ 'error': amountError }"
              type="number"
              min="0"
              @blur="validateAmount"
              @input="validateAmount"
            >
            <i>
              {{ trans('global.currency_symbol') }}&nbsp;{{ trans('global.price_tax') }}
            </i>
          </div>

          <span v-show="amountError" class="error-message">{{ amountError }}</span>
        </div>

        <div class="slider-edit-subscription__form--start-date">
          <label>{{ trans('gestion.subscription.payment.start_date') }}</label>

          <VueDatePicker
            v-model="localStartDate"
            name="start_date"
            locale="fr"
            :enable-time-picker="false"
            auto-apply
            disabled
            format="dd/MM/yyyy"
          >
            <template #input-icon>
              <span class="datepicker__icon datepicker__icon--datepicker" />
            </template>
          </VueDatePicker>
        </div>

        <div class="slider-edit-subscription__form--end-date">
          <label>{{ trans('gestion.subscription.payment.end_date') }}</label>

          <VueDatePicker
            v-model="localEndDate"
            name="end_date"
            locale="fr"
            :enable-time-picker="false"
            :min-date="minDisabledDate"
            auto-apply
            :disabled="localIsUnlimited"
            format="dd/MM/yyyy"
            @update:modelValue="onEndDateChange"
          >
            <template #input-icon>
              <span class="datepicker__icon datepicker__icon--datepicker" />
            </template>
          </VueDatePicker>

          <div class="slider-edit-subscription__form--checkbox">
            <input
              id="unlimited-checkbox"
              v-model="localIsUnlimited"
              type="checkbox"
              @change="onUnlimitedChange"
            >
            <label for="unlimited-checkbox">{{ trans('gestion.adaf.slider.subscription.unlimited') }}</label>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="displayShortEndConfirmation" class="slider-edit-subscription-short-confirmation">
      <span class="slider-edit-subscription-short-confirmation__title">
        {{ trans('gestion.invoices.slider.update_subscription.confirmation.short') }}
      </span>
      <panel
        icon="ph_warning-fill_yellow"
        type="warning"
        :content-list="[
          trans('gestion.invoices.slider.update_subscription.confirmation.short.warning.1'),
          trans('gestion.invoices.slider.update_subscription.confirmation.short.warning.2')
        ]"
      />
      <panel
        v-if="hasSubscriptionOffices"
        icon="ph_lightbulb-fill_blue"
        type="info"
        :content="transchoice('gestion.invoices.slider.update_subscription.confirmation.short.info', {
          name: subscription.title,
          date: parseDateToString(new Date(localEndDate))
        })"
      />
    </div>
    <div v-else-if="displayNoEndConfirmation" class="slider-edit-subscription-short-confirmation">
      <span class="slider-edit-subscription-short-confirmation__title">
        {{ trans('gestion.invoices.slider.update_subscription.confirmation.short') }}
      </span>
      <panel
        icon="ph_warning-fill_yellow"
        type="warning"
        :content-list="[
          transchoice('gestion.invoices.slider.update_subscription.confirmation.unlimited.warning.1', { amount: currency(localAmount) }),
          trans('gestion.invoices.slider.update_subscription.confirmation.unlimited.warning.2')
        ]"
      />
    </div>
    <div v-else-if="displayLongEndConfirmation" class="slider-edit-subscription-short-confirmation">
      <span class="slider-edit-subscription-short-confirmation__title">
        {{ trans('gestion.invoices.slider.update_subscription.confirmation.short') }}
      </span>
      <panel
        icon="ph_warning-fill_yellow"
        type="warning"
        :content-list="[
          transchoice('gestion.invoices.slider.update_subscription.confirmation.long.warning.1', { amount: currency(localAmount) }),
          transchoice(
            'gestion.invoices.slider.update_subscription.confirmation.long.warning.2',
            {
              date: getFirstOfMonthString(localEndDate)
            }
          ),
          transchoice('gestion.invoices.slider.update_subscription.confirmation.long.warning.3', {
            end_date: parseDateToString(new Date(localEndDate)),
            last_invoice_date: getFirstOfMonthString(localEndDate)
          })
        ]"
      />
      <panel
        v-if="hasSubscriptionOffices"
        icon="ph_lightbulb-fill_blue"
        type="info"
        :content="transchoice('gestion.invoices.slider.update_subscription.confirmation.short.info', {
          name: subscription.title, date: parseDateToString(new Date(localEndDate))
        })"
      />
    </div>

    <div class="slider-edit-subscription__footer">
      <div class="slider-edit-subscription__footer-buttons">
        <Button variant="secondary" @click="onClose">
          {{ trans('global.cancel') }}
        </Button>
        <Button
          variant="primary"
          :class="{ 'loading-btn': isLoading }"
          :disabled="isLoading"
          @click="onValidate"
        >
          {{ trans('global.validate') }}
        </Button>
      </div>
    </div>
  </div>
</template>
